import React, { useState, useCallback, useEffect } from 'react';

import { ChevronRight, Minus } from '@hpi-sox/hpstellar-vis-icons';
import Accordion, {
    AccordionHeader,
    AccordionBody,
    useAccordionContext,
} from '@hpi-sox/hpstellar-core/visId/Accordion';
import ButtonIcon from '@hpi-sox/hpstellar-core/visId/ButtonIcon';
import CurrencyInput from '@hpi-sox/hpstellar-core/visId/InputQuantity';

import { clickOnEnter } from '../../ui/util';
import { parsePriceFilterSelection } from '../util';

import { Helpers } from '../../core/src/helpers';
import { getGTMClickAttributes } from '../../metrics/metrics-helpers';

import './css/search-page-product-price-filter.less';

const getClosestInteger = (price, isStart) => {
    let closestInteger = isStart ? Math.floor(price) : Math.ceil(price);
    if (!['string', 'number'].includes(typeof price) || isNaN(closestInteger)) {
        return ''; // empty input box
    }
    return closestInteger;
};

const getPriceFacetData = priceRangeFilter => {
    const { FacetType, FieldType, Ranges, RangeMax, RangeMin, Values, Field } = priceRangeFilter || {};
    if (FacetType === 'slider') {
        const priceRangeFacet = priceRangeFilter && priceRangeFilter.Values && priceRangeFilter.Values[0];
        let { Count, RangeEnd, RangeMax, RangeMin, RangeStart, Selected } = priceRangeFacet || {};
        return {
            priceRangeFacet,
            RangeEnd,
            RangeMax,
            Count,
            RangeMin,
            RangeStart,
            Selected,
            FacetType,
            Field,
        };
    }

    // checkboxes design
    const presetRange =
        Values &&
        Values.reduce((selectedRanges, rangeFacet, idx) => {
            if (rangeFacet && rangeFacet.Selected) {
                selectedRanges.push(idx);
            }
            return selectedRanges;
        }, []);
    return {
        FacetType,
        rangeFacets: Array.isArray(Values)
            ? Values.map(
                  facet =>
                      facet && {
                          ...facet,
                          label: facet.Label,
                          value: facet.Value,
                      },
              )
            : [],
        presetRange,
        Field,
    };
};

const PriceFilterAccordionContent = ({
    Tooltip,
    Name,
    onChange,
    currentSearch,
    hasAccordion = true,
    priceRangeFilter,
    onSelectFacet,
    orionEnabled,
}) => {
    let { RangeEnd, RangeMax, RangeMin, RangeStart, rangeFacets, FacetType, presetRange, Field } =
        getPriceFacetData(priceRangeFilter);

    const values = useAccordionContext();
    const { FacetSelections } = currentSearch || {};
    const currentPriceSelection = parsePriceFilterSelection(FacetSelections, Field);
    const { expanded } = values || {};

    const getRoundedRange = useCallback(range => {
        return range ? [getClosestInteger(range[0], true), getClosestInteger(range[1])] : ['', ''];
    }, []);

    // initial price in the input boxes is the current selected price range, or the min/max provided by hawksearch is it is a slider, or emptyRange for checkbox filter
    const initialRange = [RangeStart, RangeEnd];
    const defaultPriceRange = currentPriceSelection
        ? getRoundedRange(currentPriceSelection)
        : getRoundedRange(initialRange);
    const [priceRange, setPriceRange] = useState(defaultPriceRange);

    const onInputChange = (value, isStart) => {
        try {
            if (value && value.length > 0 && !/^[0-9]+\.*[0-9]*$/i.test(value)) {
                return;
            }
            let roundedValue =
                typeof value === 'string' && value.length < 1 ? '' : getClosestInteger(value, isStart ? -1 : 1);
            if (/\.$/.test(value)) {
                roundedValue += '.';
            }
            setPriceRange(isStart ? [roundedValue, priceRange[1]] : [priceRange[0], roundedValue]);
        } catch (e) {}
    };

    const onChangeMin = e => {
        onInputChange(e.currentTarget.value, true);
    };

    const onChangeMax = e => {
        onInputChange(e.currentTarget.value);
    };

    const getFinalPriceInput = useCallback(
        num =>
            (typeof num === 'string' || (typeof num === 'number' && !isNaN(num))) && (num + '').length > 0 ? num : 0,
        [],
    );

    const onSubmit = () => {
        try {
            let range = [...(priceRange || [])];
            range[0] = getFinalPriceInput(range[0]);
            range[1] = getFinalPriceInput(range[1]);
            onChange(range);
        } catch (e) {}
    };

    const hasSelection = !!currentPriceSelection;
    // update slider filter.
    useEffect(() => {
        if (!currentPriceSelection) {
            // when changing filters and no price range selected, change it to match HS response
            setPriceRange(getRoundedRange([RangeMin, RangeMax]));
        }
    }, [RangeMin, RangeMax]);

    useEffect(() => {
        if (!hasSelection) {
            setPriceRange(getRoundedRange([RangeMin, RangeMax], 1));
        }
    }, [hasSelection]);

    const gtmTracking = getGTMClickAttributes('facet', 'price', `${priceRange[0]}-${priceRange[1]}`, true);

    const [startRange, endRange] = priceRange || [];
    const priceFilter = (
        <div className={`srpVisIdPriceFilter${orionEnabled ? ' orion' : ''}`}>
            <div className="srpPriceFilterInputs">
                <CurrencyInput
                    value={startRange}
                    onChange={onChangeMin}
                    symbolPosition="left"
                    symbol="$"
                    readOnly={false}
                    disabled={false}
                    type="text"
                    aria-label="Minimum price"
                />
                <Minus className="price-range-minus-icon" size="s" />
                <CurrencyInput
                    value={endRange}
                    onChange={onChangeMax}
                    symbolPosition="left"
                    symbol="$"
                    readOnly={false}
                    disabled={false}
                    type="text"
                    aria-label="Maximum price"
                />
            </div>
            <ButtonIcon
                icon={<ChevronRight />}
                variant="primary"
                theme="dark"
                size="medium"
                onClick={onSubmit}
                {...gtmTracking}
                aria-label={`Apply price filter from ${startRange} to ${endRange}`}
            />
        </div>
    );

    let extraHeaderProps = {};
    if (Tooltip) {
        extraHeaderProps = {
            TooltipProps: {
                position: 'left',
                content: <span dangerouslySetInnerHTML={Helpers.createMarkup(Tooltip)} />,
                title: Name,
            },
        };
    }
    return hasAccordion ? (
        <>
            <AccordionHeader onKeyUp={clickOnEnter} title={Name} {...extraHeaderProps} />
            <AccordionBody className="accordionContent">
                {/**Prevents tabbing into hidden buttons when the accordion is collapsed.*/}
                {expanded && priceFilter}
            </AccordionBody>
        </>
    ) : (
        priceFilter
    );
};

export default ({
    priceRangeSelector,
    priceRangeFilter,
    currentSearch,
    hasAccordion = true,
    facetSelector,
    onSelectFacet,
    orionEnabled = false,
}) => {
    const { Field, Name, Tooltip, IsCollapsible, IsCollapsedDefault, collapsedDefault, isCollapsible } =
        priceRangeFilter || {};

    const updatePriceRange = priceRangeSelector(Field);

    const defaultExpanded = [IsCollapsible, isCollapsible, collapsedDefault, IsCollapsedDefault].includes(false);

    const priceFilterContent = (
        <PriceFilterAccordionContent
            Tooltip={Tooltip}
            Name={Name}
            onChange={updatePriceRange}
            currentSearch={currentSearch}
            Field={Field}
            hasAccordion={hasAccordion}
            priceRangeFilter={priceRangeFilter}
            facetSelector={facetSelector}
            onSelectFacet={onSelectFacet}
            orionEnabled={orionEnabled}
        />
    );

    return hasAccordion ? (
        <Accordion
            title={Name}
            defaultExpanded={defaultExpanded}
            size="small"
            className="search-page-product-price-filter accordion"
            {...(IsCollapsible === false ? { expanded: true } : {})}
        >
            {priceFilterContent}
        </Accordion>
    ) : (
        priceFilterContent
    );
};
